// Building the submission and deciding where its answer sends the visitor.
// The body mirrors the lead submission contract exactly; the decision table is
// the contract's result handling, one row per response shape. Both are
// pure so every row is tested.

import { z } from "zod";

import { landingAnswersOf, type Answers, type RelationAnswer } from "./answers";
import {
  eventIdOf,
  landingQuotePath,
  verifyPathFor,
  quotePath,
  stepPath,
} from "./destinations";
import {
  isAtNeedLead,
  leadKindOf,
  lovedOneZip,
  needsSubmitterName,
  isStepComplete,
  stepsFor,
  type LeadKind,
} from "./flow";
import { parseDob } from "./validation";

export type GrantMethod = "sms_otp" | "email_link";

type Relation = { type: string; other_text?: string };

type Submitter = {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
};

type Common = {
  submitter: Submitter;
  terms_accepted: true;
  time_zone?: string;
  language?: string;
};

type DeathBody = Common & {
  situation: "death_occurred";
  decedent: { first_name: string; last_name: string };
  where: { type: string; zip: string };
  relation: Relation;
  ready_for_care: boolean;
  preplanned_with_after: { answer: boolean; reference?: string };
};

type PlannedPerson = {
  dob: string;
  zip: string;
  hospice_or_12_months: boolean;
};

type PlanningBody = Common & {
  situation: "planning_ahead";
  planning_for: "self" | "loved_one" | "both";
  self?: PlannedPerson;
  loved_one?: PlannedPerson & {
    first_name: string;
    last_name: string;
    relation?: Relation;
  };
};

export type SubmitBody = DeathBody | PlanningBody;

export type SubmitEnvironment = {
  timeZone?: string;
  language?: string;
  /** A signed-in submitter's own name, which stands in for the one the
   * answers would otherwise give. */
  submitterName?: { firstName: string; lastName: string };
};

function relationBody(relation: RelationAnswer): Relation | undefined {
  if (relation.type === null) return undefined;
  return relation.type === "other"
    ? { type: "other", other_text: relation.otherText.trim() }
    : { type: relation.type };
}

function isoDob(text: string): string | null {
  const parsed = parseDob(text);
  return parsed.ok ? parsed.iso : null;
}

function submitterBody(a: Answers, env: SubmitEnvironment): Submitter {
  const names =
    env.submitterName ?? (needsSubmitterName(a) ? a.submitter : a.self);
  return {
    first_name: names.firstName.trim(),
    last_name: names.lastName.trim(),
    email: a.submitter.email.trim(),
    phone: a.submitter.phone.trim(),
  };
}

function commonBody(a: Answers, env: SubmitEnvironment): Common {
  return {
    submitter: submitterBody(a, env),
    terms_accepted: true,
    ...(env.timeZone ? { time_zone: env.timeZone } : {}),
    ...(env.language ? { language: env.language } : {}),
  };
}

function deathBody(a: Answers, env: SubmitEnvironment): DeathBody | null {
  const { where } = a;
  const relation = relationBody(a.relation);
  if (
    where.type === null ||
    relation === undefined ||
    a.readyForCare === null ||
    a.preplanned === null
  ) {
    return null;
  }
  const reference = a.preplanReference.trim();
  return {
    ...commonBody(a, env),
    situation: "death_occurred",
    decedent: {
      first_name: a.decedent.firstName.trim(),
      last_name: a.decedent.lastName.trim(),
    },
    where: {
      type: where.type,
      zip: where.zip,
    },
    relation,
    ready_for_care: a.readyForCare,
    preplanned_with_after: {
      answer: a.preplanned,
      ...(a.preplanned && reference !== "" ? { reference } : {}),
    },
  };
}

function planningBody(a: Answers, env: SubmitEnvironment): PlanningBody | null {
  if (a.planningFor === null) return null;
  const body: PlanningBody = {
    ...commonBody(a, env),
    situation: "planning_ahead",
    planning_for: a.planningFor,
  };
  if (a.planningFor !== "loved_one") {
    const dob = isoDob(a.self.dob);
    if (dob === null || a.self.hospice === null) return null;
    body.self = {
      dob,
      zip: a.self.zip,
      hospice_or_12_months: a.self.hospice,
    };
  }
  if (a.planningFor !== "self") {
    const dob = isoDob(a.lovedOne.dob);
    if (dob === null || a.lovedOne.hospice === null) return null;
    const relation = a.lovedOne.hospice
      ? relationBody(a.lovedOne.relation)
      : undefined;
    if (a.lovedOne.hospice && relation === undefined) return null;
    body.loved_one = {
      first_name: a.lovedOne.firstName.trim(),
      last_name: a.lovedOne.lastName.trim(),
      dob,
      zip: lovedOneZip(a),
      hospice_or_12_months: a.lovedOne.hospice,
      ...(relation ? { relation } : {}),
    };
  }
  return body;
}

/** The submission for these answers, or null while any answer the
 * contract requires is missing or invalid. Self and both copy the
 * visitor's own name into the submitter. Branches the situation does not
 * use are left out. */
export function toSubmitBody(
  a: Answers,
  env: SubmitEnvironment = {},
): SubmitBody | null {
  if (!stepsFor(a).every((step) => isStepComplete(a, step))) return null;
  if (a.situation === "death_occurred") return deathBody(a, env);
  if (a.situation === "planning_ahead") return planningBody(a, env);
  return null;
}

const recordSchema = z.object({
  type: z.enum(["policy", "case"]),
  for: z.enum(["self", "loved_one", "decedent"]),
  event_id: z.string().nullable(),
  quote_id: z.string().nullable(),
});

const verificationSchema = z.object({
  grant: z.string().regex(/^[0-9a-f]{64}$/),
  methods: z.array(z.enum(["sms_otp", "email_link"])),
  phone_display: z.string().nullable(),
  email_display: z.string().nullable(),
  expires_at: z.string(),
});

const publicSubmitResponseSchema = z.object({
  submission_id: z.string().nullable(),
  status: z.enum(["completed", "awaiting_confirmation", "already_received"]),
  records: z.array(recordSchema),
  verification: verificationSchema.nullable().default(null),
  next: z.object({
    kind: z.enum([
      "quote",
      "case",
      "verify",
      "call_us",
      "other_options",
      "home",
    ]),
    quote_id: z.string().nullable(),
    case_id: z.string().nullable(),
    event_id: z.string().nullable(),
  }),
  acknowledged: z.boolean(),
});

type PublicSubmitResponse = z.infer<typeof publicSubmitResponseSchema>;

/** Where a submission stands with the visitor's confirmation. `completed`
 * needed none; `awaiting_confirmation` waits on a code or link; `closed` was
 * ended by staff and can only be picked up by phone. */
export type ConfirmationStatus =
  "awaiting_confirmation" | "confirmed" | "completed" | "expired" | "closed";

/** What the visitor opens once their submission is confirmed: the quote or
 * case it made, or their account home. */
export type ResultLanding = {
  kind: "quote" | "case" | "other";
  quoteId: string | null;
  caseId: string | null;
  eventId: string | null;
};

export type LandingVariant =
  "case_received" | "call_us" | "already_received" | "degraded";

export type RecordType = "policy" | "case";
export type RecordFor = "self" | "loved_one" | "decedent";

/** A policy or case the submission made or reused, and who it is for. */
export type LandingRecord = {
  type: RecordType;
  for: RecordFor;
};

export type LandingQuote = {
  type: RecordType;
  for: RecordFor;
  quoteId: string;
  eventId: string | null;
};

/** What the landing screen needs from a submission's answer. The login
 * grant is an opaque token and is kept only so the visitor can confirm
 * their phone or email from the landing. */
export interface LeadResult {
  variant: LandingVariant;
  leadKind: LeadKind;
  acknowledged: boolean;
  submissionId: string | null;
  eventId: string | null;
  records: LandingRecord[];
  quotes: LandingQuote[];
  /** Where the submission stands. A result starts `awaiting_confirmation`
   * when the submission offered a grant, else `completed`; the live status
   * moves it on. */
  status: ConfirmationStatus;
  /** Set once the submission is confirmed or completed. */
  landing: ResultLanding | null;
  /** What the landing copy reads from the visitor's answers, with their
   * contact and address details blanked: see `landingAnswersOf`. */
  landingAnswers: Answers;
  verification: {
    grant: string;
    /** The ways the grant can confirm the visitor, as the grant offers them.
     * Nothing is offered beyond this list. */
    methods: GrantMethod[];
    phoneDisplay: string | null;
    emailDisplay: string | null;
  } | null;
  /** The case a signed-in submitter can open from the landing; null for
   * anyone not signed in, who reaches a case only through an account. */
  caseId: string | null;
}

/** Where "View your quote" opens: the case quote the submission listed,
 * else the case page, whose own button finds the quote. A quote id is never
 * guessed from the event id. Null when there is neither. */
function caseQuotePathOf(result: LeadResult): string | null {
  const quote = result.quotes.find((candidate) => candidate.type === "case");
  if (quote) return quotePath(quote.quoteId, quote.eventId);
  return result.caseId ? `/cases/${encodeURIComponent(result.caseId)}` : null;
}

/** The quote the landing offers: a signed-in submitter's case quote, or the
 * quote a confirmed submission landed on. Null while the submission awaits
 * confirmation, and for a visitor with no account to open it in. */
export function openableQuotePathOf(result: LeadResult): string | null {
  if (result.caseId) return caseQuotePathOf(result);
  if (result.status === "confirmed") return landingQuotePath(result.landing);
  return null;
}

export type SubmitOutcome =
  "completed" | "awaiting_confirmation" | "degraded" | "rejected";

/** Why a submission was rejected or degraded, as a bounded value for
 * analytics; the visitor-facing reason is separate. */
export type SubmitFailureReason =
  | "phone_invalid"
  | "phone_unsupported_region"
  | "rate_limited"
  | "in_progress"
  | "unavailable"
  | "network"
  | "unexpected";

export type FieldRejection = { field: "phone" };

export type SubmitDecision =
  /** Navigate straight on (a quote, other options, home). */
  | { kind: "go"; outcome: SubmitOutcome; to: string }
  /** Sign in through the login grant, then resume the submission. */
  | {
      kind: "verify";
      outcome: SubmitOutcome;
      to: string;
      grant: string;
      result: LeadResult;
    }
  /** The `/start/done` landing, with the result it renders. */
  | {
      kind: "done";
      outcome: SubmitOutcome;
      /** Set when the landing is a degraded one. */
      failure?: SubmitFailureReason;
      to: string;
      result: LeadResult;
    }
  | {
      kind: "field_error";
      outcome: SubmitOutcome;
      failure: "phone_invalid" | "phone_unsupported_region";
      rejection: FieldRejection;
    }
  /** The submission is still being processed; ask again with the same key. */
  | {
      kind: "retry_same_key";
      outcome: SubmitOutcome;
      failure: "in_progress";
    }
  | {
      kind: "error";
      outcome: SubmitOutcome;
      failure: SubmitFailureReason;
      reason: "unavailable" | "rate_limited" | "unexpected";
      /** The server saw this key with different content, so the next
       * attempt needs a fresh one. */
      rotateKey: boolean;
    };

export type SubmitAnswer = { status: number | null; body: unknown };

function errorCodeOf(body: unknown): string | null {
  if (typeof body !== "object" || body === null) return null;
  const code = (body as Record<string, unknown>).error_code;
  return typeof code === "string" ? code.toLowerCase() : null;
}

function fallbackIsCall(body: unknown): boolean {
  return (
    typeof body === "object" &&
    body !== null &&
    (body as Record<string, unknown>).fallback === "call"
  );
}

function resultFrom(
  variant: LandingVariant,
  leadKind: LeadKind,
  response: PublicSubmitResponse | null,
  answers: Answers,
  signedIn = false,
): LeadResult {
  const records = response?.records ?? [];
  const quotes: LandingQuote[] = records.flatMap((record) =>
    record.quote_id === null
      ? []
      : [
          {
            type: record.type,
            for: record.for,
            quoteId: record.quote_id,
            eventId: record.event_id,
          },
        ],
  );
  const verification = response?.verification ?? null;
  return {
    variant,
    leadKind,
    acknowledged: response?.acknowledged ?? false,
    submissionId: response?.submission_id ?? null,
    eventId: response ? eventIdOf(response) : null,
    records: records.map(({ type, for: recordFor }) => ({
      type,
      for: recordFor,
    })),
    quotes,
    status: verification ? "awaiting_confirmation" : "completed",
    landing: null,
    landingAnswers: landingAnswersOf(answers),
    verification: verification
      ? {
          grant: verification.grant,
          methods: verification.methods,
          phoneDisplay: verification.phone_display,
          emailDisplay: verification.email_display,
        }
      : null,
    caseId:
      signedIn && response?.next.kind === "case" ? response.next.case_id : null,
  };
}

function doneDecision(
  outcome: SubmitOutcome,
  variant: LandingVariant,
  leadKind: LeadKind,
  response: PublicSubmitResponse | null,
  answers: Answers,
  signedIn = false,
): Extract<SubmitDecision, { kind: "done" }> {
  const result = resultFrom(variant, leadKind, response, answers, signedIn);
  return {
    kind: "done",
    outcome,
    to: stepPath("done", result.eventId),
    result,
  };
}

function degradedDecision(
  failure: SubmitFailureReason,
  leadKind: LeadKind,
  answers: Answers,
): SubmitDecision {
  return {
    ...doneDecision("degraded", "degraded", leadKind, null, answers),
    failure,
  };
}

function decideSuccess(
  response: PublicSubmitResponse,
  leadKind: LeadKind,
  answers: Answers,
  signedIn: boolean,
): SubmitDecision {
  if (response.status === "already_received") {
    return doneDecision(
      "completed",
      "already_received",
      leadKind,
      response,
      answers,
    );
  }
  const { next } = response;
  const eventId = eventIdOf(response);
  switch (next.kind) {
    case "quote":
      if (next.quote_id === null) {
        return doneDecision(
          "completed",
          "call_us",
          leadKind,
          response,
          answers,
        );
      }
      return {
        kind: "go",
        outcome: "completed",
        to: quotePath(next.quote_id, eventId),
      };
    case "verify":
      if (response.verification === null || response.submission_id === null) {
        return doneDecision(
          "completed",
          "call_us",
          leadKind,
          response,
          answers,
        );
      }
      return {
        kind: "verify",
        outcome: "awaiting_confirmation",
        to: verifyPathFor(response.submission_id),
        grant: response.verification.grant,
        result: resultFrom(
          leadKind === "planning" ? "call_us" : "case_received",
          leadKind,
          response,
          answers,
        ),
      };
    case "case":
      // A signed-in submitter is already confirmed: they get the case landing
      // and a way into the case, never the phone-only fallback.
      return doneDecision(
        "completed",
        response.verification !== null || signedIn
          ? "case_received"
          : "call_us",
        leadKind,
        response,
        answers,
        signedIn,
      );
    case "other_options":
      return {
        kind: "go",
        outcome: "completed",
        to: stepPath("other-options", eventId),
      };
    case "home":
      return {
        kind: "go",
        outcome: "completed",
        to: "/",
      };
    case "call_us":
      return doneDecision("completed", "call_us", leadKind, response, answers);
  }
}

/** Why the decision's outcome is `rejected` or `degraded`; undefined for
 * any other outcome. */
export function failureOf(
  decision: SubmitDecision,
): SubmitFailureReason | undefined {
  return "failure" in decision ? decision.failure : undefined;
}

/** Where a response to the submission sends the visitor. `answers` only
 * decides whether the visitor's lead makes a case (which phone line and
 * copy apply), never what is sent. A death or imminent plan never ends
 * on a dead end: every failure either lands on the call-us screen or
 * offers a retry beside the phone line. */
export function decideAfterSubmit(
  answer: SubmitAnswer,
  answers: Answers,
  signedIn = false,
): SubmitDecision {
  const leadKind = leadKindOf(answers);
  const atNeed = isAtNeedLead(answers);
  const { status, body } = answer;

  if (status === 200) {
    const parsed = publicSubmitResponseSchema.safeParse(body);
    if (parsed.success)
      return decideSuccess(parsed.data, leadKind, answers, signedIn);
    return atNeed
      ? degradedDecision("unexpected", leadKind, answers)
      : {
          kind: "error",
          outcome: "rejected",
          failure: "unexpected",
          reason: "unexpected",
          rotateKey: false,
        };
  }
  if (status === 503 && fallbackIsCall(body)) {
    return degradedDecision("unavailable", leadKind, answers);
  }
  const errorCode = errorCodeOf(body);
  if (
    status === 422 &&
    (errorCode === "phone_invalid" || errorCode === "phone_unsupported_region")
  ) {
    return {
      kind: "field_error",
      outcome: "rejected",
      failure: errorCode,
      rejection: { field: "phone" },
    };
  }
  if (status === 422 && errorCode === "idempotency_key_reuse") {
    return {
      kind: "error",
      outcome: "rejected",
      failure: "unexpected",
      reason: "unexpected",
      rotateKey: true,
    };
  }
  if (status === 409 && errorCode === "submission_in_progress") {
    return {
      kind: "retry_same_key",
      outcome: "rejected",
      failure: "in_progress",
    };
  }
  if (status === 429) {
    return {
      kind: "error",
      outcome: "rejected",
      failure: "rate_limited",
      reason: "rate_limited",
      rotateKey: false,
    };
  }
  if (status === null || status >= 500) {
    return {
      kind: "error",
      outcome: "rejected",
      failure: status === null ? "network" : "unavailable",
      reason: "unavailable",
      rotateKey: false,
    };
  }
  return {
    kind: "error",
    outcome: "rejected",
    failure: "unexpected",
    reason: "unexpected",
    rotateKey: false,
  };
}
